import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatCurrency } from '@/lib/utils/formatters';
import type { Allocation } from '@/types/payments';

interface AutoAllocatePreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  strategy: string;
  allocations: Allocation[];
  totalAllocated: number;
  remainingAmount: number;
  onApply: () => void;
}

export function AutoAllocatePreviewModal({
  open,
  onOpenChange,
  strategy,
  allocations,
  totalAllocated,
  remainingAmount,
  onApply,
}: AutoAllocatePreviewModalProps) {
  const handleApply = () => {
    onApply();
    onOpenChange(false);
  };

  const getStrategyDisplayName = (strategy: string) => {
    const names: Record<string, string> = {
      oldest: 'Oldest First',
      largest: 'Largest First',
      smallest: 'Smallest First',
      promise: 'Promise Priority',
      due_date: 'Due Date Priority',
    };
    return names[strategy] || strategy;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Preview Auto-Allocation</DialogTitle>
          <DialogDescription>
            Review the proposed allocations before applying them
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border p-4 bg-muted/50">
            <div className="text-sm font-medium mb-2">
              Strategy: {getStrategyDisplayName(strategy)}
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Total Allocated</div>
                <div className="font-semibold">{formatCurrency(totalAllocated)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Transactions</div>
                <div className="font-semibold">{allocations.length}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Remaining</div>
                <div className={`font-semibold ${remainingAmount > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {formatCurrency(remainingAmount)}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-sm font-medium mb-2">Proposed Allocations:</div>
            <ScrollArea className="h-[300px] rounded-md border">
              <div className="p-4 space-y-2">
                {allocations.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    No allocations to preview
                  </div>
                ) : (
                  allocations.map((allocation, index) => (
                    <div
                      key={`${allocation.transactionId}-${index}`}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {allocation.transactionNumber}
                          </span>
                          {allocation.isPromised && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                              🎯 Priority {allocation.promisePriority}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Balance: {formatCurrency(allocation.transactionBalance)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-green-600">
                          {formatCurrency(allocation.allocatedAmount)}
                        </div>
                        {allocation.allocatedAmount === allocation.transactionBalance && (
                          <div className="text-xs text-muted-foreground">
                            Fully paid
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {remainingAmount > 0 && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
              <strong>Note:</strong> {formatCurrency(remainingAmount)} will remain unapplied after this allocation.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply}>
            Apply Allocations
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
